import { parseUciScoreLine, type EvalScore } from './chessEval';

/** Stockfish 18 lite single-threaded (public/chess/stockfish.js + stockfish.wasm). */
export const STOCKFISH_WORKER_URL = '/chess/stockfish.js';

/** UCI Stockfish in a Web Worker. */
export type EngineSearchOptions = {
  skillLevel: number;
  movetimeMs: number;
};

type InitPhase = 'uci' | 'ready' | 'done';

export class ChessStockfish {
  private worker: Worker | null = null;
  private readyPromise: Promise<void> | null = null;
  private initPhase: InitPhase = 'uci';
  private initResolve: (() => void) | null = null;
  private initReject: ((err: Error) => void) | null = null;
  private movePending: {
    searchId: number;
    resolve: (uciMove: string | null) => void;
    reject: (err: Error) => void;
  } | null = null;
  private moveSearchId = 0;
  private evalPending: {
    resolve: (score: EvalScore | null) => void;
    onScore?: (score: EvalScore) => void;
  } | null = null;
  private lastEvalScore: EvalScore | null = null;
  private lineBuffer = '';
  /** One UCI search at a time (move search vs eval were racing). */
  private searchQueue: Promise<unknown> = Promise.resolve();
  /** Bumped when pending eval/move searches should be dropped (engine must move). */
  private searchGeneration = 0;

  init(): Promise<void> {
    if (this.readyPromise) return this.readyPromise;
    this.readyPromise = new Promise((resolve, reject) => {
      this.initResolve = resolve;
      this.initReject = reject;
      try {
        this.worker = new Worker(STOCKFISH_WORKER_URL);
        this.worker.onmessage = e => this.digestLines(String(e.data));
        this.worker.onerror = () => this.failInit(new Error('Stockfish worker failed'));
        this.post('uci');
        window.setTimeout(() => {
          if (this.initPhase !== 'done') {
            this.failInit(new Error('Stockfish init timed out'));
          }
        }, 20000);
      } catch (err) {
        this.failInit(err instanceof Error ? err : new Error(String(err)));
      }
    });
    return this.readyPromise;
  }

  async bestMove(fen: string, options: EngineSearchOptions): Promise<string | null> {
    return this.runExclusive(() => this.bestMoveInner(fen, options));
  }

  async evaluate(
    fen: string,
    options: { movetimeMs?: number; onUpdate?: (score: EvalScore) => void }
  ): Promise<EvalScore | null> {
    return this.runExclusive(() => this.evaluateInner(fen, options));
  }

  /** Stop worker and clear queued eval/review work so a move search can run immediately. */
  cancelPendingSearches(): void {
    this.searchGeneration++;
    this.moveSearchId++;
    this.post('stop');
    this.movePending?.resolve(null);
    this.movePending = null;
    this.evalPending?.resolve(null);
    this.evalPending = null;
    this.lastEvalScore = null;
    this.searchQueue = Promise.resolve();
  }

  dispose(): void {
    this.movePending?.resolve(null);
    this.movePending = null;
    this.evalPending?.resolve(null);
    this.evalPending = null;
    this.searchQueue = Promise.resolve();
    this.worker?.terminate();
    this.worker = null;
    this.readyPromise = null;
    this.initPhase = 'uci';
    this.initResolve = null;
    this.initReject = null;
    this.lineBuffer = '';
    this.lastEvalScore = null;
  }

  private runExclusive<T>(fn: () => Promise<T>): Promise<T> {
    const next = this.searchQueue.then(fn, fn);
    this.searchQueue = next.then(
      () => undefined,
      () => undefined
    );
    return next;
  }

  private async bestMoveInner(
    fen: string,
    options: EngineSearchOptions
  ): Promise<string | null> {
    await this.init();
    if (!this.worker) throw new Error('Engine not available');

    const gen = this.searchGeneration;
    const searchId = ++this.moveSearchId;
    return new Promise((resolve, reject) => {
      if (gen !== this.searchGeneration) {
        resolve(null);
        return;
      }
      this.movePending = { searchId, resolve, reject };
      this.evalPending = null;
      this.lastEvalScore = null;
      const skill = Math.max(1, Math.min(10, Math.round(options.skillLevel)));
      this.post('stop');
      this.post(`setoption name Skill Level value ${skill}`);
      this.post(`position fen ${fen}`);
      this.post(`go movetime ${Math.max(200, options.movetimeMs)}`);
      window.setTimeout(() => {
        if (gen !== this.searchGeneration || !this.movePending) return;
        this.post('stop');
        this.movePending.resolve(null);
        this.movePending = null;
      }, options.movetimeMs + 8000);
    });
  }

  private async evaluateInner(
    fen: string,
    options: { movetimeMs?: number; onUpdate?: (score: EvalScore) => void }
  ): Promise<EvalScore | null> {
    await this.init();
    if (!this.worker) return null;

    const movetime = options.movetimeMs ?? 800;
    const gen = this.searchGeneration;

    return new Promise(resolve => {
      if (gen !== this.searchGeneration) {
        resolve(null);
        return;
      }
      this.evalPending = options.onUpdate
        ? { resolve, onScore: options.onUpdate }
        : { resolve };
      this.movePending = null;
      this.lastEvalScore = null;
      this.post('stop');
      this.post(`position fen ${fen}`);
      this.post(`go movetime ${movetime}`);
      window.setTimeout(() => {
        if (gen !== this.searchGeneration || !this.evalPending) return;
        const done = this.evalPending;
        this.evalPending = null;
        done.resolve(this.lastEvalScore);
      }, movetime + 5000);
    });
  }

  private failInit(err: Error): void {
    this.initReject?.(err);
    this.initResolve = null;
    this.initReject = null;
    this.dispose();
  }

  private post(cmd: string): void {
    this.worker?.postMessage(cmd);
  }

  private digestLines(chunk: string): void {
    this.lineBuffer += chunk + '\n';
    const lines = this.lineBuffer.split('\n');
    this.lineBuffer = lines.pop() ?? '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;

      if (this.initPhase === 'uci' && trimmed === 'uciok') {
        this.initPhase = 'ready';
        this.post('isready');
      } else if (this.initPhase === 'ready' && trimmed === 'readyok') {
        this.initPhase = 'done';
        this.initResolve?.();
        this.initResolve = null;
        this.initReject = null;
      }

      const score = parseUciScoreLine(trimmed);
      if (score) {
        this.lastEvalScore = score;
        this.evalPending?.onScore?.(score);
      }

      if (trimmed.startsWith('bestmove ')) {
        const parts = trimmed.split(/\s+/);
        const uci = parts[1];

        if (this.movePending) {
          const p = this.movePending;
          this.movePending = null;
          if (p.searchId !== this.moveSearchId) return;
          if (!uci || uci === '(none)') p.resolve(null);
          else p.resolve(uci);
        } else if (this.evalPending) {
          const p = this.evalPending;
          this.evalPending = null;
          p.resolve(this.lastEvalScore);
        }
      }
    }
  }
}

/** Parse UCI e2e4 or e7e8q into chess.js move fields. */
export function uciToMove(uci: string): {
  from: string;
  to: string;
  promotion?: 'q' | 'r' | 'b' | 'n';
} {
  const from = uci.slice(0, 2);
  const to = uci.slice(2, 4);
  const promo = uci[4];
  const promotion =
    promo === 'q' || promo === 'r' || promo === 'b' || promo === 'n' ? promo : undefined;
  return promotion ? { from, to, promotion } : { from, to };
}
